import type { Editor } from "@tiptap/core";

/**
 * Split clipboard text into an inline run: text nodes joined by `hardBreak`s,
 * the shape typing Shift+Enter produces. The pasted lines land inside the
 * current paragraph/list item instead of fragmenting it into new blocks.
 *
 * Only blank lines at the very start and end are dropped — copying a paragraph
 * out of a PDF or a web page routinely grabs a trailing newline, but spaces
 * within a line are the user's text and pasting mid-sentence depends on them.
 */
function toInlineNodes(text: string): Array<Record<string, unknown>> {
  const lines = text.replace(/^(\r?\n)+/, "").replace(/(\r?\n)+$/, "");
  if (!lines) return [];
  const nodes: Array<Record<string, unknown>> = [];
  lines.split(/\r?\n/).forEach((line, i) => {
    if (i > 0) nodes.push({ type: "hardBreak" });
    if (line) nodes.push({ type: "text", text: line });
  });
  return nodes;
}

/**
 * The clipboard's `text/html` flavor, or null when there isn't one — a copy
 * from a plain-text source, or a browser that won't hand over rich types.
 */
async function readClipboardHtml(): Promise<string | null> {
  if (typeof navigator.clipboard?.read !== "function") return null;
  try {
    const items = await navigator.clipboard.read();
    for (const item of items) {
      if (item.types.includes("text/html")) {
        return await (await item.getType("text/html")).text();
      }
    }
  } catch {
    // read() is unsupported here or was refused for rich types. Callers fall
    // back to the plain-text read, which some browsers still allow.
  }
  return null;
}

/**
 * Insert the clipboard at the cursor with none of its formatting.
 *
 * Providers routinely copy from another EHR or a Word doc, and a normal paste
 * drags that document's fonts, colors, and heading sizes into the note. This
 * reads only `text/plain`, then strips marks across just the inserted range so
 * the text also sheds any bold/color the cursor happened to be sitting inside.
 *
 * Reading the clipboard needs a user gesture and can still be refused by the
 * browser, so callers get `false` back and should point the user at
 * Cmd/Ctrl+Shift+V instead.
 */
export async function pasteWithoutFormatting(editor: Editor): Promise<boolean> {
  let text: string;
  try {
    text = await navigator.clipboard.readText();
  } catch {
    return false;
  }

  const nodes = toInlineNodes(text);
  // An empty clipboard is still a successful read — there's no browser problem
  // to surface to the user, just nothing to insert.
  if (nodes.length === 0) return true;

  const start = editor.state.selection.from;
  editor.chain().focus().insertContent(nodes).run();
  const end = editor.state.selection.from;
  editor
    .chain()
    .setTextSelection({ from: start, to: end })
    .unsetAllMarks()
    .setTextSelection(end)
    .run();

  return true;
}

/**
 * Insert the clipboard at the cursor keeping its formatting — the same result
 * as Cmd/Ctrl+V, offered as a button so the paste menu presents both choices
 * side by side rather than making the formatting-preserving one invisible.
 *
 * The HTML is parsed against the editor's schema, so only marks and nodes the
 * note editor actually supports survive; anything else arrives as plain text.
 */
export async function pasteWithFormatting(editor: Editor): Promise<boolean> {
  const html = await readClipboardHtml();
  if (html) {
    editor
      .chain()
      .focus()
      .insertContent(html, { parseOptions: { preserveWhitespace: "full" } })
      .run();
    return true;
  }
  // Nothing rich on the clipboard: plain text is the entire content, so both
  // menu choices mean the same paste here.
  return pasteWithoutFormatting(editor);
}
